import React from "react";
import {
  StyledMediaItemBox,
  StyledMediaItemContainer,
  StyledImage,
} from "./MediaItem.styled";
import VideoContainer from "./components/VideoContainer/VideoContainer";

export const PLAYBACK_MODES = Object.freeze({
  IMAGE: "image",
  IMAGE_VIDEO_HOVER: "imageVideoHover",
  VIDEO: "video",
});

export const OBJECT_FIT = Object.freeze({
  CONTAIN: "contain",
  COVER: "cover",
  FILL: "fill",
  NONE: "none",
  SCALE_DOWN: "scale-down",
});

export type TPlaybackMode =
  (typeof PLAYBACK_MODES)[keyof typeof PLAYBACK_MODES];
export type TObjectFit = (typeof OBJECT_FIT)[keyof typeof OBJECT_FIT];

type Enumerate<
  N extends number,
  Acc extends number[] = [],
> = Acc["length"] extends N
  ? Acc[number]
  : Enumerate<N, [...Acc, Acc["length"]]>;

type IntRange<F extends number, T extends number> = Exclude<
  Enumerate<T>,
  Enumerate<F>
>;

export interface IMediaItemProps {
  /**
   * Link to the image to display. Mandatory on `image` and `imageVideoHover` playbackMode. If passing only imageUrl, default mode will be set to `image`.
   */
  imageUrl?: string;
  /**
   * Link to the video to display. Mandatory on `video` and `imageVideoHover` playbackMode. If passing only videoUrl, default mode will be set to `video`.
   */
  videoUrl?: string;
  playbackMode?: TPlaybackMode;
  /**
   * Image props to pass down to the element
   */
  imageProps?: {
    objectFit?: TObjectFit;
    borderColor?: string;
    borderOnHoverColor?: string;
    alt?: string;
    width?: any;
    height?: any;
  };
  videoProps?: {
    showLoader?: boolean;
    backgroundHoverOpacity?: IntRange<0, 101>; // Set opacity value from 0 to 100
    ariaLabel?: string;
    startFromBeginning?: boolean;
  };
}

export default function MediaItem({
  imageUrl,
  imageProps,
  videoProps,
  videoUrl,
  playbackMode,
}: IMediaItemProps) {
  if (!playbackMode) {
    if (videoUrl) {
      if (imageUrl) {
        playbackMode = PLAYBACK_MODES.IMAGE_VIDEO_HOVER;
      } else {
        playbackMode = PLAYBACK_MODES.VIDEO;
      }
    } else {
      playbackMode = PLAYBACK_MODES.IMAGE;
    }
  }

  const isImageOrHoverMode = [
    PLAYBACK_MODES.IMAGE,
    PLAYBACK_MODES.IMAGE_VIDEO_HOVER,
  ].some((e) => e === playbackMode);

  const isVideoOrHoverMode = [
    PLAYBACK_MODES.VIDEO,
    PLAYBACK_MODES.IMAGE_VIDEO_HOVER,
  ].some((e) => e === playbackMode);

  if (isImageOrHoverMode && !imageUrl) {
    throw Error("Missing image url");
  }

  if (isVideoOrHoverMode && !videoUrl) {
    throw Error("Missing video url");
  }

  return (
    <StyledMediaItemBox
      width={imageProps?.width || "100%"}
      height={imageProps?.height || "100%"}
      borderColor={imageProps?.borderColor}
      borderOnHoverColor={imageProps?.borderOnHoverColor}
    >
      <StyledMediaItemContainer>
        {isImageOrHoverMode ? (
          <StyledImage
            src={imageUrl}
            alt={imageProps?.alt || ""}
            loading="lazy"
            objectFit={imageProps?.objectFit || OBJECT_FIT.COVER}
          />
        ) : (
          videoUrl && (
            <VideoContainer
              videoUrl={videoUrl}
              videoBackgroundHoverOpacity={videoProps?.backgroundHoverOpacity}
              showLoader={videoProps?.showLoader}
              aria-label={videoProps?.ariaLabel}
            />
          )
        )}

        {playbackMode === PLAYBACK_MODES.IMAGE_VIDEO_HOVER && videoUrl && (
          <VideoContainer
            videoUrl={videoUrl}
            showOnHover={true}
            videoBackgroundHoverOpacity={videoProps?.backgroundHoverOpacity}
            showLoader={videoProps?.showLoader}
            aria-label={videoProps?.ariaLabel}
            startFromBeginning={videoProps?.startFromBeginning}
          />
        )}
      </StyledMediaItemContainer>
    </StyledMediaItemBox>
  );
}
